import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, Phone, Mail, ChevronDown } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BARANGAY, BRAND } from '@/data/mockData';

const PRIMARY_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/captain', label: 'Punong Barangay Office' },
  { to: '/officials', label: 'Officials' },
];

const NEWS_LINKS = [
  { to: '/announcements', label: 'Announcements' },
  { to: '/programs', label: 'Programs & Events' },
  { to: '/projects', label: 'Projects' },
  { to: '/accomplishments', label: 'Accomplishments' },
];

const SERVICE_LINKS = [
  { to: '/e-services', label: 'E-Services Hub' },
  { to: '/request-document', label: 'Request a Document' },
  { to: '/report-concern', label: 'Report a Concern' },
  { to: '/track-request', label: 'Track Request / Concern' },
  { to: '/emergency', label: 'Emergency Contacts' },
  { to: '/contact', label: 'Contact Us' },
];

const linkClass = ({ isActive }) =>
  `relative whitespace-nowrap text-sm font-medium tracking-tight transition-colors ${
    isActive ? 'text-primary' : 'text-foreground/70 hover:text-primary'
  }`;

export const Header = () => {
  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70"
      data-testid="public-header"
    >
      {/* Top utility bar */}
      <div className="hidden md:block bg-primary-deep text-primary-foreground/90 text-xs">
        <div className="container-app flex h-9 items-center justify-between">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <Phone className="h-3 w-3" /> {BARANGAY.hotline}
            </span>
            <span className="flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> {BARANGAY.email}
            </span>
          </div>
          <span className="opacity-90">{BARANGAY.officeHours}</span>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-app flex h-20 items-center justify-between gap-3">
        <Link to="/" className="flex min-w-0 items-center gap-3 group" data-testid="header-logo-link">
          <img
            src={BRAND.logoUrl}
            alt="Barangay San Isidro Logo"
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-full ring-2 ring-primary/15 object-contain bg-white p-0.5 shadow-sm-soft group-hover:ring-primary/40 transition"
          />
          <div className="min-w-0 leading-tight">
            <div className="font-display text-sm sm:text-base font-bold text-primary-deep truncate">
              {BARANGAY.name}
            </div>
            <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground truncate">
              {BARANGAY.municipality}, {BARANGAY.province}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-6" data-testid="primary-nav">
          {PRIMARY_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s|'/g, '-')}`}>
              {l.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger className="whitespace-nowrap flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-primary transition-colors" data-testid="nav-news-trigger">
              News & Updates <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {NEWS_LINKS.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} className="cursor-pointer" data-testid={`nav-${l.to.replace('/', '')}`}>
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger className="whitespace-nowrap flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-primary transition-colors" data-testid="nav-services-trigger">
              Services <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {SERVICE_LINKS.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} className="cursor-pointer" data-testid={`nav-${l.to.replace('/', '')}`}>
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Button asChild variant="outline" className="border-primary/20 text-primary hover:bg-primary/5" data-testid="header-track-cta">
            <Link to="/track-request">Track Request</Link>
          </Button>
          <Button asChild className="bg-gradient-primary text-primary-foreground hover:opacity-95 shadow-md-soft" data-testid="header-request-cta">
            <Link to="/request-document">Request a Document</Link>
          </Button>
        </div>

        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88%] sm:w-[420px] p-0 flex flex-col">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <div className="bg-gradient-hero p-6 text-primary-foreground">
              <div className="flex items-center gap-3">
                <img src={BRAND.logoUrl} alt="logo" className="h-12 w-12 rounded-full bg-white/95 p-0.5" />
                <div>
                  <div className="font-display font-bold">{BARANGAY.name}</div>
                  <div className="text-xs opacity-80">{BARANGAY.municipality}, {BARANGAY.province}</div>
                </div>
              </div>
              <p className="mt-4 text-sm/relaxed opacity-90">{BARANGAY.tagline}</p>
            </div>
            <nav className="flex-1 overflow-auto px-4 py-4 scrollbar-thin">
              <MobileGroup title="Main" items={PRIMARY_LINKS} />
              <MobileGroup title="News & Updates" items={NEWS_LINKS} />
              <MobileGroup title="Services" items={SERVICE_LINKS} />
            </nav>
            <div className="border-t p-4 grid grid-cols-2 gap-2">
              <Button asChild variant="outline" data-testid="mobile-track-cta"><Link to="/track-request">Track</Link></Button>
              <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="mobile-request-cta"><Link to="/request-document">Request</Link></Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

const MobileGroup = ({ title, items }) => (
  <div className="mb-4">
    <div className="px-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
    <ul className="grid">
      {items.map((l) => (
        <li key={l.to}>
          <NavLink
            to={l.to}
            className={({ isActive }) =>
              `block rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                isActive ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-secondary'
              }`
            }
            data-testid={`mobile-nav-${l.to.replace('/', '') || 'home'}`}
          >
            {l.label}
          </NavLink>
        </li>
      ))}
    </ul>
  </div>
);
